import { InsertDriveFile, PlayArrow } from "@mui/icons-material"
import { Box, ClickAwayListener, Modal, Stack } from "@mui/material"
import { useEffect, useState } from "react"

export type MediaCardData = { path: string; type: string }

export function MediaCard({
  media,
  size
}: {
  media: MediaCardData
  size?: { width: number; height: number }
}) {
  //prettier-ignore
  const imageFormats = ["png", "jpg", "jpeg", "gif", "bmp", "tiff", "svg", "webp", "ico", "heic", "psd", "raw"]
  //prettier-ignore
  const videoFormats = ["mp4", "avi", "mkv", "mov", "wmv", "flv", "webm", "m4v", "mpeg", "3gp", "mts", "ts", "vob"]
  //prettier-ignore
  const audioFormats = ["mp3", "wav", "ogg", "flac", "aac", "wma", "m4a", "aiff", "alac", "pcm", "aif", "au", "amr"]
  //prettier-ignore
  const documentFormats = ["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt", "rtf", "csv", "xml", "json", "html", "htm", "md", "log", "zip", "rar", "7z", "tar", "gz", "bz2", "xz", "tgz", "txz", "pdf", "epub", "mobi", "azw", "azw3", "djvu", "fb2", "pdb", "prc", "cbz", "cbr", "cb7", "cbt", "cba", "chm", "lit", "lrf", "odt", "ods", "odp", "odg", "odf", "odb", "odc", "odm", "ott", "ots", "otp", "otg", "otf", "otc", "otm", "oth", "ott", "ots", "otp", "otg", "otf", "otc", "otm", "oth", "ott", "ots", "otp", "otg", "otf", "otc", "otm", "oth", "ott", "ots", "otp", "otg", "otf", "otc", "otm", "oth", "ott", "ots", "otp", "otg", "otf", "otc", "otm", "oth", "ott", "ots", "otp", "otg", "otf", "otc", "otm", "oth", "ott", "ots", "otp", "otg", "otf", "otc", "otm", "oth", "ott", "ots", "otp", "otg", "otf", "otc", "otm", "oth", "ott", "ots", "otp", "otg", "otf", "otc", "otm", "oth", "ott", "ots", "otp", "otg", "otf", "otc", "otm", "oth"]

  function checkContentTypeFromUrl(
    path?: MediaCardData["path"],
    type?: MediaCardData["type"]
  ) {
    const extension = path?.split(".").pop()?.toLowerCase()
    if (imageFormats.includes(extension!) || type?.includes("image")) {
      return <ImageCard media={media} size={size} />
    }

    if (videoFormats.includes(extension!) || type?.includes("video")) {
      return <VideoCard media={media} size={size} />
    }

    if (audioFormats.includes(extension!) || type?.includes("audio")) {
      return <AudioCard media={media} />
    }

    if (audioFormats.includes(extension!) || type?.includes("audio")) {
      return <AudioCard media={media} />
    }

    if (
      documentFormats.includes(extension!) ||
      type?.includes("application") ||
      type?.includes("document")
    ) {
      return <DocumentCard media={media} />
    }

    return <></>
  }

  return checkContentTypeFromUrl(media.path, media.type)
}

export function ImageCard({
  media,
  size
}: {
  media: MediaCardData
  size?: { width: number; height: number }
}) {
  const [open, setOpen] = useState(false)

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  return (
    <>
      <Stack
        direction={"row"}
        alignItems={"center"}
        columnGap={0.5}
        sx={{
          borderRadius: 2,
          border: "1px solid #EFEFEF",
          cursor: "pointer"
        }}
        onClick={handleOpen}
      >
        <img
          src={media.path}
          style={{
            objectFit: "cover",
            width: Number(size?.width),
            maxHeight: Number(size?.height),
            height: "auto",
            minHeight: Number(size?.height),
            borderRadius: size ? 5 : 1
          }}
        />
      </Stack>
      <Modal
        open={open}
        onClose={handleClose}
        closeAfterTransition
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "transparent",
          width: "100vw",
          height: "100vh"
        }}
      >
        <Box
          sx={{
            boxShadow: 24,
            borderRadius: 2,
            outline: 0,
            overflow: "auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}
          onClick={handleClose}
        >
          <img
            src={media.path}
            alt="Attachment"
            style={{
              width: "100%",
              maxWidth: "80vw",
              maxHeight: "80vh",
              height: "100%",
              minWidth: "300px",
              objectFit: "contain"
            }}
          />
        </Box>
      </Modal>
    </>
  )
}

export function VideoCard({
  media,
  size
}: {
  media: MediaCardData
  size?: { width: number; height: number }
}) {
  const [open, setOpen] = useState(false)

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  useEffect(() => {
    const video = document.getElementById("thumbnail") as
      | HTMLVideoElement
      | undefined
    if (video) {
      video.currentTime = 1
    }
  }, [])

  return (
    <>
      <Stack
        direction={"row"}
        alignItems={"center"}
        columnGap={0.5}
        sx={{
          p: size ? 0 : 0.5,
          borderRadius: 2,
          border: "1px solid #EFEFEF",
          cursor: "pointer",
          position: "relative"
        }}
        onClick={handleOpen}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: Number(size?.width),
            height: "auto",
            maxHeight: Number(size?.height),
            borderRadius: 1,
            background: "rgb(0,0,0,0.5)"
          }}
        >
          <video
            style={{
              objectFit: "cover",
              width: Number(size?.width),
              maxHeight: Number(size?.height),
              height: "auto",
              minHeight: Number(size?.height),
              borderRadius: size ? 5 : 1
            }}
            preload="metada"
            id={"thumbnail"}
          >
            <source src={media.path} type={media.type} />
            Your browser does not support the video tag.
          </video>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: "100%",
              height: "100%",
              background: "rgba(0,0,0,0.1)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center"
            }}
            onClick={handleOpen}
          >
            <PlayArrow sx={{ color: "white", fontSize: 50 }} />
          </Box>
        </Box>
      </Stack>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        closeAfterTransition
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          width: "100vw"
        }}
      >
        <Box
          sx={{
            bgcolor: "transparent",
            boxShadow: 24,
            borderRadius: 2,
            outline: 0,
            overflow: "auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}
        >
          <ClickAwayListener onClickAway={handleClose}>
            <video
              controls
              autoPlay
              style={{
                width: "100%",
                height: "100%",
                maxWidth: "80vw",
                maxHeight: "80vh",
                objectFit: "contain"
              }}
            >
              <source src={media.path} type={media.type} />
              Your browser does not support the video tag.
            </video>
          </ClickAwayListener>
        </Box>
      </Modal>
    </>
  )
}

export function AudioCard({ media }: { media: MediaCardData }) {
  return (
    <audio
      controls
      style={{
        width: "100%",
        maxWidth: "100%",
        alignSelf: "stretch"
      }}
    >
      <source src={media.path} type={"audio/mp3"} />
      Your browser does not support the audio element.
    </audio>
  )
}
export function DocumentCard({ media }: { media: MediaCardData }) {
  return (
    <div>
      <a
        href={media.path}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          marginTop: "1rem",
          textDecoration: "none",
          color: "#767676"
        }}
      >
        <InsertDriveFile sx={{ fontSize: 32 }} />
      </a>
    </div>
  )
}
